import React, { useState, useEffect } from 'react';
import {
    Box, Button, Card, CardContent, Chip, Container, Dialog,
    DialogActions, DialogContent, DialogTitle, Divider,
     FormControlLabel, Grid, IconButton, 
    List, ListItem, ListItemText, ListItemButton,  Paper, 
    Switch, TextField, Typography, useTheme,
    useMediaQuery, Stack, CircularProgress
} from "@mui/material";
import {
    Add, Edit, Delete,  PlaylistAdd,
    Layers, Close,
    AddCircleOutline,
    
} from "@mui/icons-material";
import { getSideCars, removeSideCar, saveSideCar } from '../../hooks/apiHandler';

/**
 * NOTE: In a production environment, you should use your real hooks:
 * import { useNavigate } from "react-router-dom";
 * import { getSideCars, saveSideCar, removeSideCar } from "../../hooks/apiHandler";
 */

// --- COMPONENT START ---

export default function SideCarPage() {
    
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    const storeID = (() => {
        try {
            const ds = window.localStorage.getItem('ds');
            return ds ? JSON.parse(ds) : "default";
        } catch (e) {
            return "default";
        }
    })();

    // Master State
    const [modifierSets, setModifierSets] = useState([]);
    const [selectedSetId, setSelectedSetId] = useState(null);
    const [loading, setLoading] = useState(true);
    
    // Dialog States
    const [openGroupDialog, setOpenGroupDialog] = useState(false);
    const [openSetDialog, setOpenSetDialog] = useState(false);
    const [editingGroupId, setEditingGroupId] = useState(null);
    const [editingSetId, setEditingSetId] = useState(null);

    // Form State for a "Modifier Group"
    const [groupForm, setGroupForm] = useState({
        id: "",
        name: "",
        minSelection: 1,
        maxSelection: 1,
        maxFree: 0,
        allowHalfAndHalf: false,
        options: [{ id: Date.now().toString(), name: "", price: 0 }]
    });

    const [setName, setSetName] = useState("");

    // --- API Interactions ---

    const fetchData = async () => {
        try {
            setLoading(true);
            const res = await getSideCars({ storeID });
            if (res && res.code === 0) {
                const data = res.data || [];
                setModifierSets(data);
                if (data.length > 0 && !selectedSetId) {
                    setSelectedSetId(data[0]._id);
                }
            }
        } catch (error) {
            console.error("Fetch error:", error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchData();
    }, []);

    const currentSet = modifierSets.find(s => s._id === selectedSetId);

    // --- Set Management (Create/Update/Delete Root Modifier Object) ---

    const handleOpenSetDialog = (set = null) => {
        if (set) {
            setEditingSetId(set._id);
            setSetName(set.name);
        } else {
            setEditingSetId(null);
            setSetName("");
        }
        setOpenSetDialog(true);
    };

    const handleSaveSet = async () => {
        try {
            setLoading(true);
            const payload = {
                storeID,
                name: setName,
                option: editingSetId ? currentSet.option : []
            };
            if (editingSetId) payload._id = editingSetId;

            const res = await saveSideCar(payload);
            if (res && res.code === 0) {
                await fetchData();
                setOpenSetDialog(false);
            }
        } catch (error) {
            console.error("Save set error:", error);
        } finally {
            setLoading(false);
        }
    };

    const handleDeleteSet = async (setId) => {
        if (!window.confirm("Delete this entire modifier set?")) return;
        try {
            setLoading(true);
            const res = await removeSideCar({ id: setId, storeID });
            if (res && res.code === 0) {
                const updated = modifierSets.filter(s => s._id !== setId);
                setModifierSets(updated);
                if (selectedSetId === setId && updated.length > 0) {
                    setSelectedSetId(updated[0]._id);
                } else if (updated.length === 0) {
                    setSelectedSetId(null);
                }
            }
        } catch (error) {
            console.error("Delete error:", error);
        } finally {
            setLoading(false);
        }
    };

    // --- Group Management (Update internal 'option' array of a Set) ---

    const handleOpenGroupDialog = (group = null) => {
        if (group) {
            setEditingGroupId(group.id);
            setGroupForm({ ...group });
        } else {
            setEditingGroupId(null);
            setGroupForm({
                id: `g_${Date.now()}`,
                name: "",
                minSelection: 1,
                maxSelection: 1,
                maxFree: 0,
                allowHalfAndHalf: false,
                options: [{ id: `o_${Date.now()}`, name: "", price: 0 }]
            });
        }
        setOpenGroupDialog(true);
    };

    const handleSaveGroup = async () => {
        if (!currentSet) return;

        try {
            setLoading(true);
            let updatedOptions = [...currentSet.option];
            
            if (editingGroupId) {
                updatedOptions = updatedOptions.map(g => g.id === editingGroupId ? groupForm : g);
            } else {
                updatedOptions.push(groupForm);
            }

            const payload = {
                ...currentSet,
                option: updatedOptions,
                storeID
            };

            const res = await saveSideCar(payload);
            if (res && res.code === 0) {
                await fetchData();
                setOpenGroupDialog(false);
            }
        } catch (error) {
            console.error("Save group error:", error);
        } finally {
            setLoading(false);
        }
    };

    const handleDeleteGroup = async (groupId) => {
        if (!window.confirm("Remove this group?") || !currentSet) return;

        try {
            setLoading(true);
            const updatedOptions = currentSet.option.filter(g => g.id !== groupId);
            const payload = { ...currentSet, option: updatedOptions, storeID };
            
            const res = await saveSideCar(payload);
            if (res && res.code === 0) {
                await fetchData();
            }
        } catch (error) {
            console.error("Delete group error:", error);
        } finally {
            setLoading(false);
        }
    };

    const handleAddOption = () => {
        setGroupForm({
            ...groupForm,
            options: [...groupForm.options, { id: `o_${Date.now()}`, name: "", price: 0 }]
        });
    };

    const handleRemoveOption = (index) => {
        setGroupForm({
            ...groupForm,
            options: groupForm.options.filter((_, i) => i !== index)
        });
    };

    const handleOptionChange = (index, field, value) => {
        const newOptions = [...groupForm.options];
        newOptions[index][field] = value;
        setGroupForm({ ...groupForm, options: newOptions });
    };

    return (
        <Container maxWidth="lg" sx={{ py: { xs: 2, sm: 4 }, px: { xs: 1, sm: 2 } }}>
            <Box sx={{ mb: 3, display: 'flex', alignItems: 'center' }}>
                {/* <IconButton onClick={() => navigate("/admin/")} sx={{ mr: 1 }}>
                    <ArrowBackIcon />
                </IconButton> */}
                <Box>
                    <Typography variant={isMobile ? "h5" : "h4"} fontWeight="800">
                        Side-Car Management
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        Sync and manage your product modifier hierarchy.
                    </Typography>
                </Box>
            </Box>

            <Grid container spacing={isMobile ? 2 : 3}>
                {/* Sidebar: Modifier Sets */}
                <Grid item xs={12} md={4}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                        <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1, fontWeight: 'bold', fontSize: '1.1rem' }}>
                            <Layers color="primary" fontSize="small" /> Modifier Sets
                        </Typography>
                        <Button 
                            size="small" variant="outlined" startIcon={<AddCircleOutline />}
                            onClick={() => handleOpenSetDialog()}
                            disabled={loading}
                        >
                            New Set
                        </Button>
                    </Box>

                    <Paper variant="outlined" sx={{ borderRadius: 2, overflow: 'hidden', position: 'relative' }}>
                        {loading && !openGroupDialog && !openSetDialog && (
                            <Box sx={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, bgcolor: 'rgba(255,255,255,0.7)', zIndex: 1, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                                <CircularProgress size={24} />
                            </Box>
                        )}
                        <List disablePadding>
                            {modifierSets.map((set) => (
                                <ListItem 
                                    key={set._id} 
                                    disablePadding 
                                    divider
                                    secondaryAction={
                                        <Box sx={{ display: 'flex', gap: 0.5 }}>
                                            <IconButton edge="end" size="small" onClick={() => handleOpenSetDialog(set)}>
                                                <Edit fontSize="small" color="primary" />
                                            </IconButton>
                                            <IconButton edge="end" size="small" onClick={() => handleDeleteSet(set._id)}>
                                                <Delete fontSize="small" color="error" />
                                            </IconButton>
                                        </Box>
                                    }
                                >
                                    <ListItemButton
                                        selected={selectedSetId === set._id}
                                        onClick={() => setSelectedSetId(set._id)}
                                        sx={{ pr: 9 }}
                                    >
                                        <ListItemText
                                            primary={String(set.name)}
                                            secondary={`${set.option.length} groups`}
                                            primaryTypographyProps={{ 
                                                fontWeight: selectedSetId === set._id ? '700' : '400',
                                                color: selectedSetId === set._id ? 'primary.main' : 'inherit',
                                                noWrap: true
                                            }}
                                        />
                                    </ListItemButton>
                                </ListItem>
                            ))}
                            {modifierSets.length === 0 && !loading && (
                                <Box sx={{ p: 4, textAlign: 'center' }}>
                                    <Typography variant="caption" color="textSecondary">No sets found.</Typography>
                                </Box>
                            )}
                        </List>
                    </Paper>
                </Grid>

                {/* Main Content Area */}
                <Grid item xs={12} md={8}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexDirection: isMobile ? 'column' : 'row', gap: 1.5 }}>
                        <Typography variant="h6" fontWeight="700" sx={{ fontSize: '1.1rem' }}>
                            Groups in {String(currentSet?.name || "...")}
                        </Typography>
                        <Button
                            fullWidth={isMobile}
                            variant="contained"
                            startIcon={<PlaylistAdd />}
                            onClick={() => handleOpenGroupDialog()}
                            disabled={!selectedSetId || loading}
                        >
                            Add Group
                        </Button>
                    </Box>

                    {(!currentSet || currentSet.option.length === 0) ? (
                        <Paper sx={{ p: isMobile ? 4 : 8, textAlign: 'center', border: '2px dashed #eee', borderRadius: 4 }}>
                            <Typography variant="body2" color="text.disabled">
                                {selectedSetId ? "No groups found in this set." : "Select a set to manage groups."}
                            </Typography>
                        </Paper>
                    ) : (
                        <Stack spacing={2}>
                            {currentSet.option.map((group) => (
                                <Card key={group.id} variant="outlined" sx={{ borderRadius: 3, transition: '0.2s' }}>
                                    <CardContent sx={{ p: isMobile ? 2 : 3 }}>
                                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1.5 }}>
                                            <Box>
                                                <Typography variant="subtitle1" fontWeight="bold">{String(group.name)}</Typography>
                                                <Stack direction="row" spacing={0.5} sx={{ mt: 1, flexWrap: 'wrap', gap: 0.5 }}>
                                                    <Chip size="small" label={`Min: ${group.minSelection}`} variant="outlined" sx={{ height: 20, fontSize: '0.7rem' }} />
                                                    <Chip size="small" label={`Max: ${group.maxSelection}`} variant="outlined" sx={{ height: 20, fontSize: '0.7rem' }} />
                                                    {group.maxFree > 0 && <Chip size="small" color="success" label={`${group.maxFree} Free`} sx={{ height: 20, fontSize: '0.7rem' }} />}
                                                    {group.allowHalfAndHalf && <Chip size="small" color="secondary" label="H&H" sx={{ height: 20, fontSize: '0.7rem' }} />}
                                                </Stack>
                                            </Box>
                                            <Box sx={{ display: 'flex' }}>
                                                <IconButton size="small" color="primary" onClick={() => handleOpenGroupDialog(group)}><Edit fontSize="small" /></IconButton>
                                                <IconButton size="small" color="error" onClick={() => handleDeleteGroup(group.id)}><Delete fontSize="small" /></IconButton>
                                            </Box>
                                        </Box>
                                        <Divider sx={{ my: 1.5 }} />
                                        <Grid container spacing={1.5}>
                                            {group.options.map(opt => (
                                                <Grid item xs={12} sm={6} key={opt.id}>
                                                    <Box sx={{ p: 1.2, bgcolor: '#fcfcfc', borderRadius: 2, border: '1px solid #f0f0f0', display: 'flex', justifyContent: 'space-between' }}>
                                                        <Typography variant="body2">{String(opt.name)}</Typography>
                                                        <Typography variant="caption" sx={{ fontWeight: 'bold', color: 'primary.main' }}>
                                                            {opt.price > 0 ? `+$${opt.price.toFixed(2)}` : 'Free'}
                                                        </Typography>
                                                    </Box>
                                                </Grid>
                                            ))}
                                        </Grid>
                                    </CardContent>
                                </Card>
                            ))}
                        </Stack>
                    )}
                </Grid>
            </Grid>

            {/* Rename/New Set Dialog */}
            <Dialog open={openSetDialog} onClose={() => setOpenSetDialog(false)} fullWidth maxWidth="xs">
                <DialogTitle sx={{ fontWeight: 'bold' }}>
                    {editingSetId ? "Rename Set" : "New Modifier Set"}
                </DialogTitle>
                <DialogContent>
                    <TextField
                        autoFocus fullWidth sx={{ mt: 2 }} label="Set Name"
                        value={setName} onChange={(e) => setSetName(e.target.value)}
                        onKeyDown={(e) => { if (e.key === 'Enter' && setName) handleSaveSet(); }}
                    />
                </DialogContent>
                <DialogActions sx={{ p: 3 }}>
                    <Button onClick={() => setOpenSetDialog(false)} color="inherit">Cancel</Button>
                    <Button variant="contained" onClick={handleSaveSet} disabled={!setName || loading}>
                        {loading ? <CircularProgress size={20} /> : "Save Set"}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Group Config Dialog */}
            <Dialog open={openGroupDialog} onClose={() => setOpenGroupDialog(false)} maxWidth="sm" fullWidth fullScreen={isMobile}>
                <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="h6" fontWeight="bold">
                        {editingGroupId ? "Edit Group" : "Add New Group"}
                    </Typography>
                    <IconButton onClick={() => setOpenGroupDialog(false)} size="small"><Close /></IconButton>
                </DialogTitle>
                <DialogContent dividers>
                    <Grid container spacing={2} sx={{ pt: 1 }}>
                        <Grid item xs={12}>
                            <TextField
                                fullWidth label="Group Name" placeholder="e.g. Select Size"
                                value={groupForm.name} onChange={(e) => setGroupForm({ ...groupForm, name: e.target.value })}
                            />
                        </Grid>
                        <Grid item xs={4}><TextField fullWidth type="number" label="Min" value={groupForm.minSelection} onChange={(e) => setGroupForm({ ...groupForm, minSelection: parseInt(e.target.value) || 0 })} /></Grid>
                        <Grid item xs={4}><TextField fullWidth type="number" label="Max" value={groupForm.maxSelection} onChange={(e) => setGroupForm({ ...groupForm, maxSelection: parseInt(e.target.value) || 0 })} /></Grid>
                        <Grid item xs={4}><TextField fullWidth type="number" label="Free" value={groupForm.maxFree} onChange={(e) => setGroupForm({ ...groupForm, maxFree: parseInt(e.target.value) || 0 })} /></Grid>
                        <Grid item xs={12}><FormControlLabel control={<Switch checked={groupForm.allowHalfAndHalf} onChange={(e) => setGroupForm({ ...groupForm, allowHalfAndHalf: e.target.checked })} />} label="Allow Half & Half" /></Grid>
                        <Grid item xs={12}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2, alignItems: 'center' }}>
                                <Typography variant="subtitle2" fontWeight="bold">Options</Typography>
                                <Button size="small" variant="outlined" startIcon={<Add />} onClick={handleAddOption}>Add Option</Button>
                            </Box>
                            <Stack spacing={1.5}>
                                {groupForm.options.map((opt, idx) => (
                                    <Box key={idx} sx={{ display: 'flex', gap: 1, alignItems: 'center', flexDirection: { xs: 'column', sm: 'row' }, p: { xs: 1.5, sm: 0 }, border: { xs: '1px solid #eee', sm: 'none' }, borderRadius: 2 }}>
                                        <TextField fullWidth={isMobile} size="small" sx={{ flex: 2 }} label="Option Name" value={opt.name} onChange={(e) => handleOptionChange(idx, 'name', e.target.value)} />
                                        <Stack direction="row" spacing={1} sx={{ width: isMobile ? '100%' : 'auto', flex: 1 }}>
                                            <TextField fullWidth size="small" label="Price ($)" type="number" value={opt.price} onChange={(e) => handleOptionChange(idx, 'price', parseFloat(e.target.value) || 0)} />
                                            <IconButton size="small" color="error" onClick={() => handleRemoveOption(idx)} disabled={groupForm.options.length === 1}><Delete fontSize="small" /></IconButton>
                                        </Stack>
                                    </Box>
                                ))}
                            </Stack>
                        </Grid>
                    </Grid>
                </DialogContent>
                <DialogActions sx={{ p: 3 }}>
                    <Button onClick={() => setOpenGroupDialog(false)} color="inherit">Cancel</Button>
                    <Button variant="contained" onClick={handleSaveGroup} disabled={!groupForm.name || loading}>
                        {loading ? <CircularProgress size={20} /> : "Apply Changes"}
                    </Button>
                </DialogActions>
            </Dialog>
        </Container >
    );
}